import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  InputAdornment,
} from "@mui/material";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import { Button } from "../components/Buttons";
import {
  getBalance,
  getCommissionEarnings,
  getMyCommissionWithdrawals,
  raiseCommissionWithdrawal,
} from "../api";

const INR = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

/* Colour + label for each withdrawal-request status. */
const REQUEST_STATUS = {
  pending: { label: "Pending", color: "warning" },
  paid: { label: "Paid", color: "success" },
  rejected: { label: "Rejected", color: "error" },
};

/* Where a commission row came from — helps spot test/manual rows. */
const SOURCE_LABEL = {
  payin: "Payin",
  bbps: "Bill",
  manual: "Manual",
};

export default function Commissions() {
  const navigate = useNavigate();
  const [earnings, setEarnings] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  /* Gateway-fee netting — only the admin bears Cashfree's cut, so this
     breakdown is shown for admins only. */
  const isAdmin = localStorage.getItem("roleName") === "ADMIN";
  const [feeInfo, setFeeInfo] = useState({
    cashfreeFeePercent: 0,
    cashfreeFee: 0,
    netAfterGatewayFee: 0,
  });

  /* Withdrawable commission balance (sum of all commission wallets) + tickets. */
  const [balance, setBalance] = useState(0);
  const [requests, setRequests] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const pendingRequest = requests.find((r) => r.status === "pending");

  /* Requested amount must be > 0 and no more than the available balance. */
  const amountNum = Number(amount);
  const amountValid = amount !== "" && amountNum > 0 && amountNum <= balance;

  function openDialog() {
    setFeedback(null);
    setAmount(balance > 0 ? String(balance) : "");
    setNote("");
    setDialogOpen(true);
  }

  const fetchEarnings = useCallback(() => {
    setLoading(true);
    getCommissionEarnings(page)
      .then(({ data }) => {
        setEarnings(data.earnings || []);
        setTotal(data.pagination?.total || data.total || 0);
        setTotalEarned(data.totalEarned || 0);
        setFeeInfo({
          cashfreeFeePercent: data.cashfreeFeePercent || 0,
          cashfreeFee: data.cashfreeFee || 0,
          netAfterGatewayFee: data.netAfterGatewayFee || 0,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  const fetchSidecar = useCallback(() => {
    getBalance()
      .then(({ data }) => setBalance(data.totalCommission ?? 0))
      .catch(() => {});
    getMyCommissionWithdrawals()
      .then(({ data }) => setRequests(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchEarnings();
  }, [fetchEarnings]);

  useEffect(() => {
    fetchSidecar();
  }, [fetchSidecar]);

  async function handleRaise() {
    setSubmitting(true);
    setFeedback(null);
    try {
      await raiseCommissionWithdrawal(amountNum, note.trim() || undefined);
      setDialogOpen(false);
      setNote("");
      setFeedback({
        severity: "success",
        msg: "Withdrawal request raised — an admin will review and clear it.",
      });
      fetchSidecar();
    } catch (e) {
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Could not raise the request",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5">Commission Earnings</Typography>
          <Typography variant="body2" color="text.secondary">
            Lifetime earned: {INR(totalEarned)}
          </Typography>
        </Box>
      </Stack>

      {/* Admin only: net margin after the Cashfree gateway fee. */}
      {isAdmin && (
        <Card sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
            Net earning (after gateway fee)
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={3}
            divider={
              <Box sx={{ width: "1px", bgcolor: "divider", alignSelf: "stretch" }} />
            }
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Gross commission
              </Typography>
              <Typography variant="h6">{INR(totalEarned)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Less Cashfree fee ({feeInfo.cashfreeFeePercent}%)
              </Typography>
              <Typography variant="h6" sx={{ color: "var(--error)" }}>
                −{INR(feeInfo.cashfreeFee)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Net earning
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color:
                    feeInfo.netAfterGatewayFee >= 0
                      ? "var(--success)"
                      : "var(--error)",
                }}
              >
                {INR(feeInfo.netAfterGatewayFee)}
              </Typography>
            </Box>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Your commission wallet holds the gross amount; {feeInfo.cashfreeFeePercent}% of
            payin volume is Cashfree's cost, so your real margin is the net.
          </Typography>
        </Card>
      )}

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.msg}
        </Alert>
      )}

      {/* Withdrawable balance + request action */}
      <Card sx={{ p: 2.5, mb: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#1c6b8c",
                bgcolor: "color-mix(in srgb, #1c6b8c 14%, transparent)",
              }}
            >
              <AccountBalanceWalletRoundedIcon />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Withdrawable commission
              </Typography>
              <Typography variant="h5">{INR(balance)}</Typography>
            </Box>
          </Stack>

          <Stack alignItems="flex-end" gap={0.5}>
            <Button
              variant="contained"
              color="primary"
              disabled={!(balance > 0)}
              onClick={() => navigate("/payouts")}
              sx={{ minWidth: 200 }}
            >
              Withdraw Commission
            </Button>
            <Typography variant="caption" color="text.secondary">
              Withdraw to your bank via Payouts — sent through Gmax after admin
              approval.
            </Typography>
          </Stack>
        </Stack>
      </Card>

      {/* My withdrawal requests */}
      {requests.length > 0 && (
        <Card sx={{ mb: 3, overflow: "hidden" }}>
          <Box sx={{ px: 2.5, pt: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              My Withdrawal Requests
            </Typography>
          </Box>
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Requested</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Admin note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => {
                  const st = REQUEST_STATUS[r.status] || {
                    label: r.status,
                    color: "default",
                  };
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                        {formatDate(r.created_at)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {INR(r.amount)}
                      </TableCell>
                      <TableCell>
                        {r.amount_paid != null ? INR(r.amount_paid) : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={st.label} color={st.color} />
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                        {r.admin_note || "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Card>
      )}

      <Card sx={{ overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : earnings.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <PaidRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">
              No commission earnings yet
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell>From</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Payin Amount</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Commission</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {earnings.map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {e.from_first_name} {e.from_last_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${SOURCE_LABEL[e.source] || e.source}${
                          e.gateway ? ` · ${e.gateway}` : ""
                        }`}
                        sx={{ fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {INR(e.payin_amount)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={`${Number(e.percentage || 0)}%`}
                        sx={{
                          fontWeight: 600,
                          bgcolor: "#e3f0f6",
                          color: "#1c6b8c",
                          fontSize: 12,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "success.main" }}>
                      +{INR(e.commission_amount)}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                      {formatDate(e.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {Math.ceil(total / 50) > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={Math.ceil(total / 50)}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Raise request dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Request Commission Withdrawal</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Available commission balance: <b>{INR(balance)}</b>. Enter how much
              you want to withdraw — an admin settles it and marks it paid, and
              the amount is deducted from your commission wallet at that point.
            </Alert>
            <TextField
              fullWidth
              type="number"
              label="Amount to withdraw"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={amount !== "" && !amountValid}
              helperText={
                amount !== "" && !amountValid
                  ? `Enter an amount between ₹1 and ${INR(balance)}`
                  : `You can withdraw up to ${INR(balance)}`
              }
              inputProps={{ min: 1, max: balance, step: "0.01" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
            />
            <Button
              variant="text"
              size="small"
              onClick={() => setAmount(String(balance))}
              sx={{ alignSelf: "flex-start" }}
            >
              Withdraw full balance
            </Button>
            <TextField
              fullWidth
              label="Note (optional)"
              multiline
              minRows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Please transfer to my registered bank account"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            loading={submitting}
            disabled={!amountValid}
            onClick={handleRaise}
          >
            Raise Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
